import {
  EditOutlined,
  PlayCircleFilled,
  SaveOutlined,
  StopOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  TimePicker,
  Tooltip,
  message
} from 'antd';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import CustomDatePicker from '../../components/CustomDatePicker';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import { errorHandler } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 }
  },
  wrapperCol: {
    xs: { span: 24 }
  }
};

const styles = {
  form: { textAlign: 'left' },
  card: { marginBottom: 10, textAlign: 'left' }
};

const AseguradoraDetenerProceso = () => {
  const [form] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState();
  const [data, setData] = useState();
  const [visible, setVisible] = useState(false);
  const [fechasModificadas, setFechasModificadas] = useState(false);
  const [buttonstate, setButtonstate] = useState(false);
  const [loadingState, setLoadingState] = useState(0);
  const tableRef = useRef();

  const {
    params: { id: idAseguradora }
  } = useRouteMatch();

  const url = `/job/cierre/aseguradora/${idAseguradora}`;
  const columns = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      filterType: fieldTypes.STRING,
      sortable: true,
      render: (thisestado) => {
        let estadoUpperCase;
        let color = 'white';
        if (thisestado) {
          color = 'green';
          estadoUpperCase = 'ACTIVO';
        } else {
          color = 'orange';
          estadoUpperCase = 'INACTIVO';
        }
        return (
          <Tag color={color} key={estadoUpperCase}>
            {estadoUpperCase}
          </Tag>
        );
      }
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'inicio',
      filterType: fieldTypes.DATETIME,
      sortable: true
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'fin',
      filterType: fieldTypes.DATETIME,
      sortable: true
    },
    {
      title: 'Acciones',
      key: 'action',
      width: '10px',
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="middle"
              onClick={() => {
                update(record);
              }}
              disabled={
                (!record.activo &&
                  moment(record.inicio).format('YYYY-MM-DD HH:mm') <
                    moment().format('YYYY-MM-DD HH:mm')) ||
                moment(record.fin).format('YYYY-MM-DD HH:mm') <
                  moment().format('YYYY-MM-DD HH:mm')
              }
            />
          </Tooltip>
          <Tooltip title="Detener">
            <Button
              type="text"
              icon={<StopOutlined />}
              size="middle"
              loading={loadingState === record.id}
              onClick={() => detener(record)}
              disabled={!record.activo}
            />
          </Tooltip>
          <Tooltip title="Iniciar ahora">
            <Button
              type="text"
              icon={<PlayCircleFilled />}
              size="middle"
              loading={loadingState === record.id}
              onClick={() => iniciar(record)}
              disabled={
                record.activo ||
                moment(record.inicio).format('YYYY-MM-DD HH:mm') <
                  moment().format('YYYY-MM-DD HH:mm')
              }
            />
          </Tooltip>
        </Space>
      ),
      align: 'center'
    }
  ];

  const detener = async (record) => {
    try {
      setLoadingState(record.id);
      await instanceRef.instance.put(`job/${record.id}`, {
        activo: false,
        fin: moment()
      });
    } catch (error) {
      errorHandler(error);
    }
    setLoadingState(0);
  };

  const iniciar = async (record) => {
    try {
      setLoadingState(record.id);
      // Se realizar una inactivacion y borrado logico del actual job
      await instanceRef.instance.put(`job/${record.id}`, {
        activo: false,
        inicio: moment(),
        eliminado: true
      });

      // Se crea un nuevo Job y se programa con la nueva fecha de Inicio
      const values = {
        ...record,
        activo: true,
        inicio: moment(),
        fin: moment(record.fin),
        hora_inicio: moment().add(60, 'seconds'),
        hora_fin: moment(record.fin, 'HH:mm:ss')
      };
      onFinish(values, false, false);
      message.success('Cierre iniciado');
    } catch (error) {
      errorHandler(error);
    }
    setLoadingState(0);
  };

  const checkDate = () => {
    if (formEdit.getFieldValue('inicio') === null) {
      setButtonstate(false);
      return Promise.resolve();
    }
    if (formEdit.getFieldValue('fin') === null) {
      setButtonstate(false);
      return Promise.resolve();
    }
    if (formEdit.getFieldValue('inicio') > formEdit.getFieldValue('fin')) {
      setButtonstate(true);
      return Promise.reject(
        new Error('El fin de Cierre tiene que ser posterior al del incio')
      );
    }

    const inicio = formEdit.getFieldValue('inicio');
    const inicioFormat = inicio.format('YYYY-MM-DD');
    const todayFormat = moment().format('YYYY-MM-DD');

    if (todayFormat > inicioFormat) {
      setButtonstate(false);
      return Promise.reject(
        new Error('El Inicio de Cierre tiene que ser una fecha futura')
      );
    }
    if (
      todayFormat === inicioFormat &&
      formEdit.getFieldValue('hora_inicio') &&
      !formEdit.getFieldValue('activo')
    ) {
      const horaInicio = formEdit.getFieldValue('hora_inicio');
      const horaInicioFormat = horaInicio.format('HH:mm');
      const todayHour = moment().format('HH:mm');
      if (todayHour >= horaInicioFormat) {
        setButtonstate(false);
        return Promise.reject(
          new Error('El Inicio de Cierre tiene que ser en un horario futuro')
        );
      }
    }
    setButtonstate(false);
    return Promise.resolve();
  };

  const checkDateToAdd = () => {
    if (form.getFieldValue('inicio') === null) {
      setButtonstate(false);
      return Promise.resolve();
    }
    if (form.getFieldValue('fin') === null) {
      setButtonstate(false);
      return Promise.resolve();
    }
    if (
      form.getFieldValue('inicio') &&
      form.getFieldValue('fin') &&
      form.getFieldValue('inicio') > form.getFieldValue('fin')
    ) {
      setButtonstate(true);
      return Promise.reject(
        new Error('El fin de Cierre tiene que ser posterior al del incio')
      );
    }

    if (form.getFieldValue('inicio')) {
      const inicio = form.getFieldValue('inicio');
      const inicioFormat = inicio.format('YYYY-MM-DD');
      const todayFormat = moment().format('YYYY-MM-DD');

      if (todayFormat > inicioFormat) {
        setButtonstate(false);
        return Promise.reject(
          new Error('El Inicio de Cierre tiene que ser una fecha futura')
        );
      }
      if (todayFormat === inicioFormat && form.getFieldValue('hora_inicio')) {
        const horaInicio = form.getFieldValue('hora_inicio');
        const horaInicioFormat = horaInicio.format('HH:mm');
        const todayHour = moment().format('HH:mm');
        if (todayHour >= horaInicioFormat) {
          setButtonstate(false);
          return Promise.reject(
            new Error('El Inicio de Cierre tiene que ser en un horario futuro')
          );
        }
      }
    }

    setButtonstate(false);
    return Promise.resolve();
  };

  const onFinish = async (values, activeMessage, update) => {
    setLoading(true);
    try {
      const hour = values.hora_inicio.format('HH');
      const minute = values.hora_inicio.format('mm');
      const second = values.hora_inicio.format('ss');

      const hourEnd = values.hora_fin.format('HH');
      const minuteEnd = values.hora_fin.format('mm');
      const secondEnd = values.hora_fin.format('ss');

      // TODO: no mostrar segundos para cargar
      const inicio = values.inicio.set({
        hour,
        minute,
        second,
        milliseconds: 0
      });

      const fin = values.fin.set({
        hour: hourEnd,
        minute: minuteEnd,
        second: secondEnd,
        milliseconds: 0
      });

      const job = {
        id: values.id ? values.id : undefined,
        id_aseguradora: idAseguradora,
        tipo: 'CIERRE_ASEGURADORA',
        inicio,
        fin,
        descripcion: values.descripcion,
        activo: values.activo ? values.activo : false,
        cantidad: 0,
        fecha: moment(),
        update
      };
      await instanceRef.instance.post(`job/`, job);
      setModel({});
      if (activeMessage) {
        message.success('Cierre creado correctamente');
      }

      setLoading(false);
    } catch (error2) {
      errorHandler(error2);
    }
    setLoading(false);
  };

  const update = async (record) => {
    setVisible(true);
    setData({
      ...record,
      inicio: moment(record.inicio),
      fin: moment(record.fin),
      hora_inicio: moment(record.inicio),
      hora_fin: moment(record.fin)
    });
    formEdit.setFieldsValue({
      ...record,
      inicio: moment(record.inicio),
      fin: moment(record.fin),
      hora_inicio: moment(record.inicio),
      hora_fin: moment(record.fin)
    });
  };

  useEffect(() => {
    form.resetFields();
  }, [model, form]);

  const handleOk = async () => {
    setVisible(false);
    setLoading(true);
    try {
      if (!fechasModificadas) {
        await instanceRef.instance.put(`job/${data.id}`, {
          descripcion: formEdit.getFieldValue('descripcion')
        });
      } else if (fechasModificadas) {
        // Si las fechas fueron modificadas se actualiza y se vuelve a crear un job
        const fields = formEdit.getFieldsValue();
        const values = {
          ...data,
          ...fields
        };
        await onFinish(values, false, true); // se actualiza y se genera un nuevo job
      }

      setFechasModificadas(false);

      message.success('Cierre actualizado correctamente');
    } catch (error2) {
      errorHandler(error2);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };
  return (
    <ResponsiveContainer size="large">
      <Spin spinning={loading} delay={500} size="large">
        <Card
          title="Agregar Cierre de Aseguradora"
          style={{ textAlign: 'left' }}
        >
          <Form
            {...formItemLayout}
            initialValues={model}
            scrollToFirstError
            form={form}
            name="job"
            autoComplete="off"
            layout="vertical"
            onFinish={(values) => onFinish(values, true, false)}
          >
            <Row gutter={[16, 0]} style={{ textAlign: 'start' }}>
              <Col xs={12} md={12} align="start">
                <Form.Item
                  label="Descripción"
                  rules={[
                    {
                      required: true,
                      whitespace: true
                    }
                  ]}
                  name="descripcion"
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]} style={{ textAlign: 'start' }}>
              <Col xs={6} md={6} align="start">
                <Form.Item
                  label="Fecha Inicio"
                  name="inicio"
                  rules={[{ required: true }, { validator: checkDateToAdd }]}
                >
                  <CustomDatePicker
                    style={{ width: '100%' }}
                    placeholder="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={6} md={6} align="start">
                <Form.Item
                  label="Hora Inicio"
                  name="hora_inicio"
                  rules={[
                    { required: true, message: 'Requerido' },
                    { validator: checkDateToAdd }
                  ]}
                >
                  <TimePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]} style={{ textAlign: 'start' }}>
              <Col xs={6} md={6} align="start">
                <Form.Item
                  label="Fecha Fin"
                  name="fin"
                  rules={[{ required: true }, { validator: checkDateToAdd }]}
                >
                  <CustomDatePicker
                    style={{ width: '100%' }}
                    placeholder="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={6} md={6} align="start">
                <Form.Item
                  label="Hora Fin"
                  name="hora_fin"
                  rules={[
                    { required: true, message: 'Requerido' },
                    { validator: checkDateToAdd }
                  ]}
                >
                  <TimePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              disabled={buttonstate}
            >
              Agregar
            </Button>
          </Form>
          <br />
          <TableAndFilters
            ref={tableRef}
            url={url}
            title="Cierres programados"
            columns={columns}
            showTable
          />
        </Card>
      </Spin>
      <Modal
        title="Editar Cierre"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleOk}
            disabled={buttonstate}
          >
            Actualizar
          </Button>
        ]}
      >
        <Form
          form={formEdit}
          layout="vertical"
          name="cierreForm"
          style={styles.form}
          initialValues={data}
        >
          <Form.Item
            label="Descripción"
            rules={[
              {
                required: true,
                whitespace: true
              }
            ]}
            name="descripcion"
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Fecha Inicio"
            name="inicio"
            rules={[{ required: true }, { validator: checkDate }]}
          >
            <CustomDatePicker
              style={{ width: '100%' }}
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              onChange={() => {
                setFechasModificadas(true);
              }}
              disabled={formEdit.getFieldValue('activo')}
            />
          </Form.Item>

          <Form.Item
            label="Hora Inicio"
            name="hora_inicio"
            rules={[
              { required: true, message: 'Requerido' },
              { validator: checkDate }
            ]}
          >
            <TimePicker
              style={{ width: '100%' }}
              disabled={formEdit.getFieldValue('activo')}
              onChange={() => setFechasModificadas(true)}
            />
          </Form.Item>

          <Form.Item
            label="Fecha Fin"
            name="fin"
            rules={[{ required: true }, { validator: checkDate }]}
          >
            <CustomDatePicker
              style={{ width: '100%' }}
              placeholder="DD/MM/YYYY"
              format="DD/MM/YYYY"
              onChange={() => {
                setFechasModificadas(true);
              }}
            />
          </Form.Item>

          <Form.Item
            label="Hora Fin"
            name="hora_fin"
            rules={[
              { required: true, message: 'Requerido' },
              { validator: checkDate }
            ]}
          >
            <TimePicker
              style={{ width: '100%' }}
              onChange={() => setFechasModificadas(true)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ResponsiveContainer>
  );
};

export default AseguradoraDetenerProceso;
